import { ReactNode, useState, useEffect, useRef, useCallback } from 'react';
import { TextComponent } from "./TextComponent";
import { ArrowDown } from "../icons/ArrowDown";
import { Column } from "./Column";

interface Option {
    key: string;
    label: string;
    icon?: ReactNode;
}

interface BaseSelectionProps {
    label: string;
    items: Option[];
    selectionMode?: "single" | "multiple";
    placeholder?: string;
    required?: boolean;
    defaultSelectedKeys?: string[];
    value?: string[]; // Optional for controlled component
    onChange: (e: React.ChangeEvent<any>) => void;
    onBlur: (e: React.FocusEvent<any>) => void;
    name?: string;
}

export const Select = ({
    label,
    items,
    selectionMode = "single",
    placeholder = "Select",
    required = false,
    defaultSelectedKeys = [],
    value = [], // Initialize with an empty array if not provided
    onChange,
    onBlur,
    name
}: BaseSelectionProps) => {
    const [selection, setSelection] = useState<string[]>(defaultSelectedKeys);
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState("top-full mt-1");
    const dropdownRef = useRef<HTMLDivElement>(null);
    const selectedLabels = items
        .filter(option => selection.includes(option.key))
        .map(option => option.label)
        .join(", ") || placeholder;

    // Update internal state when `value` prop changes
    useEffect(() => {
        if (value.length > 0) {
            setSelection(value);
        }
    }, [value]);

    const handleOptionClick = useCallback((option: Option) => {
        const newSelection = selectionMode === "single"
            ? [option.key]
            : selection.includes(option.key)
                ? selection.filter(key => key !== option.key)
                : [...selection, option.key];

        setSelection(newSelection);

        onChange({
            target: {
                name,
                value: selectionMode === "single" ? option.key : newSelection,
            }
        } as React.ChangeEvent<any>);
        if (selectionMode === "single") setIsOpen(false);

    }, [selectionMode, selection, onChange, name]);

    useEffect(() => {
        if (selectionMode === "single" && selectedLabels !== placeholder) {
            onBlur({
                target: { name }
            } as React.FocusEvent<any>);
        }
    }, [selectedLabels, selectionMode, onBlur, name, placeholder]);

    const handleClickOutside = useCallback((event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setIsOpen(false);
            onBlur({
                target: { name }
            } as React.FocusEvent<any>);
        }
    }, [onBlur, name]);

    useEffect(() => {
        const updateDropdownPosition = () => {
            if (!dropdownRef.current) return;
            const rect = dropdownRef.current.getBoundingClientRect();
            const dropdownHeight = 272; // (20 - 3) * 16
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            if (spaceBelow >= dropdownHeight) {
                setDropdownPosition("top-full mt-1");
            } else if (spaceAbove >= dropdownHeight) {
                setDropdownPosition("bottom-full mb-1");
            } else {
                setDropdownPosition(spaceBelow >= spaceAbove ? "top-full mt-1" : "bottom-full mb-1");
            }
        };
        updateDropdownPosition();
        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            window.addEventListener("resize", updateDropdownPosition);
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
                window.removeEventListener("resize", updateDropdownPosition);
            };
        }
    }, [isOpen, handleClickOutside]);

    return (
        <Column className="text-start gap-2">
            <TextComponent variant="h6">
                {label} {required && <span className="text-red-500 select-none">*</span>}
            </TextComponent>
            <div className="relative" ref={dropdownRef}>
                <button
                    className="appearance-none w-full bg-white transition-colors duration-300 ease-in-out hover:bg-grayDefault border border-gray-300 text-gray-700 py-2.5 md:py-3 px-2.5 md:px-5 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500 flex justify-between items-center gap-4 select-none"
                    onClick={() => setIsOpen(!isOpen)}
                    type="button"
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                >
                    <TextComponent
                        variant="h5"
                        className="text-start overflow-hidden text-ellipsis whitespace-nowrap uppercase"
                    >
                        {selectedLabels}
                    </TextComponent>
                    <div className={`transform transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}>
                        <ArrowDown />
                    </div>
                </button>
                {isOpen && (
                    <ul
                        className={`absolute z-10 w-full bg-white border border-gray-300 rounded py-2.5 md:py-3 overflow-y-auto shadow-xl ${dropdownPosition} max-h-[10rem] md:max-h-[20rem] select-none`}
                    >
                        {items.map((option) => (
                            <li
                                key={option.key}
                                className={`flex items-center transition-colors duration-300 ease-in-out hover:bg-gray-100 cursor-pointer py-2.5 md:py-3 px-2.5 md:px-5 gap-2 md:gap-4 uppercase 
                                    ${selection.includes(option.key)
                                        ? "border-l-2 md:border-l-4 border-pink"
                                        : "border-l-2 md:border-l-4 border-transparent"
                                    }`}
                                onClick={() => handleOptionClick(option)}
                            >
                                {option.icon && (
                                    <div className="w-5 h-5 md:w-[35px] md:h-[35px] object-cover flex justify-center items-center">
                                        {option.icon}
                                    </div>
                                )}
                                <TextComponent
                                    variant="h5"
                                    className="text-start overflow-hidden text-ellipsis whitespace-nowrap"
                                >
                                    {option.label}
                                </TextComponent>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </Column>
    );
};
